/**
 * Shared device lookup for the compatibility checker and the device finder.
 *
 * Mirrors how the previous Dawn theme worked, so the same store data keeps
 * driving it with nothing to re-enter:
 *   - Brands come from the brand index page, models from each brand page.
 *     Both expose their entries as `#model-images .item` anchors.
 *   - A device collection page publishes the interfaces it supports in
 *     `#device-interfaces`.
 *   - A product publishes its own interface in `#interface`.
 *   - Compatible means the device's interface list contains the product's.
 *
 * Always fetched fresh - a sessionStorage cache used to be here, but it had
 * no expiry or invalidation, so a page fetched once (e.g. before a device's
 * interfaces metafield was filled in) kept being served stale for the rest
 * of that browser session even after the underlying data was fixed.
 */

/** @param {string} url @returns {Promise<string>} */
async function fetchHtml(url) {
  const response = await fetch(url, { headers: { Accept: 'text/html' } });
  if (!response.ok) throw new Error('Could not load ' + url);
  return response.text();
}

/** @param {string} html @returns {Document} */
function parse(html) {
  return new DOMParser().parseFromString(html, 'text/html');
}

/**
 * Reads the device tiles from a brand index or brand page. Each entry
 * carries the group it was found under - e.g. nevawireless.com/pages/shop-by-brand
 * lists "Top Selling Brand's Accessories" and "All Other Brand's Accessories"
 * as two separate #model-images grids, each preceded by its own heading.
 * group is null for a container with no heading before it (e.g. a single
 * brand's own device list), so callers that don't care about grouping can
 * ignore the field entirely.
 * @param {string} url
 * @returns {Promise<Array<{title: string, url: string, group: string | null}>>}
 */
export async function getEntries(url) {
  const doc = parse(await fetchHtml(url));

  // Attribute selector, not getElementById: these pages repeat the id across
  // "Top devices" and "All other devices", and getElementById finds only one.
  const containers = doc.querySelectorAll('[id="model-images"], .model-images');
  const entries = [];
  const seen = new Set();

  for (const container of containers) {
    // Mirrors nw-model-images.liquid's own lookup: the heading sits before
    // the grid as a sibling, not as a wrapper.
    let heading = container.previousElementSibling;
    while (heading && !/^H[1-6]$/.test(heading.tagName)) {
      heading = heading.previousElementSibling;
    }
    const group = heading?.textContent?.trim() || null;

    for (const item of container.querySelectorAll('.item')) {
      const link = item.querySelector('a');
      const href = link?.getAttribute('href');
      if (!href || seen.has(href)) continue;

      const title = (item.querySelector('span')?.textContent || link.textContent || '').trim();
      if (!title) continue;

      seen.add(href);
      entries.push({ title, url: href, group });
    }
  }

  return entries;
}

/**
 * Appends entries to a <select>, grouping consecutive entries that share the
 * same `group` under an <optgroup> - a no-op wrapper when nothing is
 * grouped, so this is safe to use for every entries list this module can
 * return.
 * @param {HTMLSelectElement} select
 * @param {Array<{title: string, url: string, group: string | null}>} entries
 */
export function appendGroupedOptions(select, entries) {
  let currentGroup;
  let target = select;

  for (const entry of entries) {
    if (entry.group !== currentGroup) {
      currentGroup = entry.group;
      if (currentGroup) {
        target = document.createElement('optgroup');
        target.label = currentGroup;
        select.append(target);
      } else {
        target = select;
      }
    }
    target.append(new Option(entry.title, entry.url));
  }
}

/**
 * Interfaces a device collection page declares it supports.
 * @param {string} url
 * @returns {Promise<string | null>} null when the page has no #device-interfaces
 */
export async function getDeviceInterfaces(url) {
  const doc = parse(await fetchHtml(url));
  return doc.querySelector('#device-interfaces')?.textContent ?? null;
}

/**
 * Same test as odemobile's checker: a case-sensitive "contains" of the
 * product's interface in the device's list. A product with no interface is
 * never compatible.
 * @param {string} deviceUrl
 * @param {string} productInterface
 * @returns {Promise<boolean|null>} null when the device publishes no interfaces
 */
export async function isCompatible(deviceUrl, productInterface) {
  const interfaces = await getDeviceInterfaces(deviceUrl);
  if (interfaces === null) return null;

  return Boolean(productInterface) && interfaces.includes(productInterface);
}
